import { AgePlaysResponse, SelectedPlatform } from '@/types/api/age';
import { themeRawValues } from '@/utils/styleUtils';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';
import { AgeDataByPlatform } from './AgeDataByPlatform';
import { ChartTooltipWrapper } from '@/components/charts/ChartTooltip';
import { TooltipContent } from './TooltipContent';
import { mixpanelTrack, RevenueDashboardWidget } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { formatPercentage } from '@/utils/numberFormattingUtils';

const chartColors = themeRawValues.colors.chartColors;

const colors = [chartColors.color3, chartColors.color5, chartColors.color6];

export const AgeChart = ({
  data,
  selectedPlatform,
  handlePlatformChange,
}: {
  data: AgePlaysResponse;
  selectedPlatform: SelectedPlatform<typeof data>;
  handlePlatformChange: (platform: SelectedPlatform<typeof data>) => void;
}) => {
  const handleTooltipHover = (event: { label?: string; percentage?: number }) => {
    mixpanelTrack(MIXPANEL_EVENT.audience.demographics.chartTooltipHover, {
      widget: RevenueDashboardWidget.AgeDemographics,
      page: 'audience',
      platform: selectedPlatform,
      demographics: 'age',
      segment: event?.label || '',
      percent: event.percentage ? Math.round(event.percentage).toString() + '%' : '',
    });
  };
  const filteredData =
    data.plays.find((play) => play.platform === selectedPlatform)?.age_distribution ?? [];

  return (
    <div className="flex flex-col justify-between h-full items-center max-w-[400px] mx-auto">
      <ResponsiveContainer height={360} width="100%" className="flex flex-col z-elevated">
        <BarChart data={filteredData} margin={{ bottom: 32, top: 32 }}>
          <Tooltip
            cursor={false}
            isAnimationActive={true}
            offset={32}
            allowEscapeViewBox={{ y: true }}
            animationDuration={1000}
            content={({ active, payload }: TooltipProps<number, string>) => (
              <ChartTooltipWrapper className="max-w-64 border-[#F0F0F9] bg-[#FAFAFA] text-textDarkGrey">
                {active && payload?.[0]?.payload && (
                  <TooltipContent
                    label={`${payload[0].payload.label} years old`}
                    value={formatPercentage(payload[0].payload.percentage)}
                    selectedPlatform={selectedPlatform}
                  />
                )}
              </ChartTooltipWrapper>
            )}
          />

          <Bar dataKey="value" fill={colors[0]} onMouseEnter={handleTooltipHover} />

          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: '14px', fontWeight: '500' }}
            tickMargin={16}
          />
        </BarChart>
      </ResponsiveContainer>
      <div className="my-16 flex flex-row justify-center">
        <AgeDataByPlatform
          data={data}
          selectedPlatform={selectedPlatform}
          handlePlatformChange={handlePlatformChange}
        />
      </div>
    </div>
  );
};
